import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Platform } from "react-native";
import { generatePath, useNavigate } from "react-router";
import { useI18n } from "@lookiero/i18n-react";
import { PaymentFlowStrategies, PaymentInstrumentSelectRef, PaymentPayload } from "@lookiero/payments-front";
import { PromoCodeError, PromoCode } from "@lookiero/promocodes-front";
import { Layout } from "@lookiero/sty-psp-ui";
import { Product } from "../../../../domain/entities/product/Product";
import {
  isStockOutResponseError, // oxlint-disable-next-line no-unused-vars
  StockOutResponseError,
} from "../../../domain/orderRepositoryImpl/OrderRepositoryImpl";
import { useApplyPromo } from "../../behaviors/useApplyPromo";
import { useCreateCheckout } from "../../behaviors/useCreateCheckout";
import { useDiscardPromo } from "../../behaviors/useDiscardPromo";
import { useGetExperiments } from "../../behaviors/useGetExperiments";
import { useGetOrderSummaryForCheckout } from "../../behaviors/useGetOrderSummaryForCheckout";
import { useRemoveProductFromCheckout } from "../../behaviors/useRemoveProductFromCheckout";
import { Checkout as CheckoutTemplate } from "../../components/templates/checkout/Checkout";
import { usePaymentInstrumentEvents } from "../../hooks/usePaymentInstrumentEvents";
import { useStaticInfo } from "../../hooks/useStaticInfo";
import { useToast } from "../../hooks/useToast";
import { DOMAIN } from "../../i18n/i18n";
import { Routes } from "../../routing/routes";
import { OS } from "../../shared/config";
import { STORAGE_KEY_ONGOING_ORDER_ID } from "../../shared/util/storageKeys";
import { TrackingPage, TrackingView } from "../../tracking/tracking";
import { useTrackCheckoutItemRemoved } from "../../tracking/useTrackCheckoutItemRemoved";
import { useTrackCheckoutSoldOut } from "../../tracking/useTrackCheckoutSoldOut";
import { useTrackCheckoutStarted } from "../../tracking/useTrackCheckoutStarted";
import { useTrackCloseStockOutModal } from "../../tracking/useTrackCloseStockOutModal";
import { useTrackPageView } from "../../tracking/useTrackPageView";
import { useTrackPaymentResult } from "../../tracking/useTrackPaymentResult";
import { useTrackPromocodeInvalid } from "../../tracking/useTrackPromocodeInvalid";
import { useTrackPromocodeValid } from "../../tracking/useTrackPromocodeValid";
import { useTrackRemoveAndPayStockOutModal } from "../../tracking/useTrackRemoveAndPayStockOutModal";
import { useTrackShowStockOutModal } from "../../tracking/useTrackShowStockOutModal";
import { getShippingAddress } from "./Checkout.controller";

interface CheckoutProps {
  readonly layout: Layout;
  readonly useRedirect: () => Record<string, string>;
}

const Checkout: FC<CheckoutProps> = ({ layout, useRedirect }) => {
  const [isCheckoutStarted, setIsCheckoutStarted] = useState(false);

  const navigate = useNavigate();

  const { returnUrl } = useRedirect();
  const { formatMessage } = useI18n({ domain: DOMAIN });
  const paymentFlowRef = useRef<PaymentFlowStrategies>(null);
  const paymentInstrumentSelectRef = useRef<PaymentInstrumentSelectRef>(null);
  const {
    isPaymentOK,
    session: { user },
    basePath,
    store,
    tradename,
  } = useStaticInfo();
  const toast = useToast();
  const { experiments } = useGetExperiments();
  const removeProductFromCheckout = useRemoveProductFromCheckout();

  const trackingCommonParams = useMemo(
    () => ({
      country: user.country,
      segment: user.segment,
      userId: user.id,
      tradename,
    }),
    [tradename, user.country, user.id, user.segment],
  );

  useTrackPageView({
    ...trackingCommonParams,
    page: TrackingPage.CHECKOUT,
    view: TrackingView.LOOK_IN_CHECKOUT,
  });
  const trackCheckoutItemRemoved = useTrackCheckoutItemRemoved(trackingCommonParams);
  const trackCheckoutSoldOut = useTrackCheckoutSoldOut(trackingCommonParams);
  const trackCheckoutStarted = useTrackCheckoutStarted(trackingCommonParams);
  const trackCloseStockOutModal = useTrackCloseStockOutModal(trackingCommonParams);
  const trackPromocodeInvalid = useTrackPromocodeInvalid(trackingCommonParams);
  const trackPromocodeValid = useTrackPromocodeValid(trackingCommonParams);
  const trackRemoveAndPayStockOutModal = useTrackRemoveAndPayStockOutModal(trackingCommonParams);
  const trackShowStockOutModal = useTrackShowStockOutModal(trackingCommonParams);
  const trackPaymentResult = useTrackPaymentResult(trackingCommonParams);

  const { orderSummaryForCheckout, isLoading: isLoadingOrderSummaryForCheckout } = useGetOrderSummaryForCheckout();

  const shippingAddress = getShippingAddress(user);

  const onDeleteProduct = useCallback(
    async (item: Product) => {
      removeProductFromCheckout(
        [item],
        () => {
          trackCheckoutItemRemoved({ product: item });
        },
        (error) => console.error(error),
      );
    },
    [removeProductFromCheckout, trackCheckoutItemRemoved],
  );

  const [stockOutModalVisible, setStockOutModalVisible] = useState<boolean>(false);

  /**
   * We need to store the ongoing order id in the storage to be able to navigate to the confirmation page
   * after the payment is successful
   */
  useEffect(() => {
    if (!orderSummaryForCheckout.id) {
      return;
    }

    store.set(STORAGE_KEY_ONGOING_ORDER_ID, orderSummaryForCheckout.id);
  }, [orderSummaryForCheckout.id, store]);
  const onPaymentSuccess = useCallback(
    async (payload: PaymentPayload) => {
      const ongoingOrderId: string = (await store.get(STORAGE_KEY_ONGOING_ORDER_ID)) as unknown as string;

      if (!ongoingOrderId) {
        return;
      }

      setTimeout(async () => {
        if (payload.metadata?.toaster) {
          toast.showSuccess({ message: formatMessage({ id: payload.metadata.toaster.id }) });
        }

        await store.remove(STORAGE_KEY_ONGOING_ORDER_ID);
        navigate(generatePath(`${basePath}/${Routes.CHECKOUT_CONFIRMATION}`, { orderId: ongoingOrderId }));
      }, 1000);
    },
    [basePath, formatMessage, navigate, store, toast],
  );
  const onPaymentError = useCallback(
    (payload: PaymentPayload) => {
      // This default id has been obtained from the original implementation
      // https://github.com/lookiero/user-area-front/pull/3698/files#diff-5abb8805b7e976f532eb0561c2e9532834100c45a00308b82e40b91696580ee4L40
      toast.showError({ message: formatMessage({ id: payload.metadata?.toaster?.id || "toast.generic.error" }) });
      trackPaymentResult({ orderId: orderSummaryForCheckout.id, success: false });
      setIsCheckoutStarted(false);
    },
    [formatMessage, orderSummaryForCheckout.id, toast, trackPaymentResult],
  );
  usePaymentInstrumentEvents({ onSuccess: onPaymentSuccess, onError: onPaymentError });

  const createCheckout = useCreateCheckout(orderSummaryForCheckout.id);
  const startCheckout = useCallback(async () => {
    setIsCheckoutStarted(true);

    try {
      const checkoutUuid = await createCheckout();

      if (checkoutUuid) {
        await paymentFlowRef.current?.startCheckout({
          checkoutUuid,
          userInformation: {
            name: user.shippingAddress.address.name,
            email: user.email,
            segment: user.segment,
          },
          returnUrl: `${basePath}/${Routes.CHECKOUT}`,
        });
      }
    } catch (error: StockOutResponseError | unknown) {
      console.log(error);
      setIsCheckoutStarted(false);

      if (!isStockOutResponseError(error)) {
        toast.showError({ message: formatMessage({ id: "toast.generic.error" }) });

        return;
      }

      setStockOutModalVisible(true);

      const { itemIds: payloadNotAvailableItemsIds } = error;
      const notAvailableItems: Product[] = orderSummaryForCheckout.items.filter((item) =>
        payloadNotAvailableItemsIds.includes(item.itemId as string),
      );

      const notAvailableItemsVariantIds = notAvailableItems.map((item) => item.variantId);
      const hasItemsWithStock = orderSummaryForCheckout.items.length !== payloadNotAvailableItemsIds?.length;

      trackCheckoutSoldOut({
        products: notAvailableItems,
        variantId: notAvailableItemsVariantIds.join(),
        stockAvailable: hasItemsWithStock,
      });
    }
  }, [
    basePath,
    createCheckout,
    formatMessage,
    orderSummaryForCheckout.items,
    toast,
    trackCheckoutSoldOut,
    user.email,
    user.segment,
    user.shippingAddress.address.name,
  ]);

  const isCheckoutDisabled =
    isCheckoutStarted ||
    orderSummaryForCheckout.items.length === 0 ||
    orderSummaryForCheckout.items.filter((product) => !product.available).length ===
      orderSummaryForCheckout.items.length ||
    !shippingAddress.length;

  useEffect(() => {
    if (orderSummaryForCheckout && !isLoadingOrderSummaryForCheckout) {
      if (Boolean(orderSummaryForCheckout.id) && orderSummaryForCheckout.items.length === 0) {
        navigate(`${basePath}/${Routes.CART}`);
      }

      trackCheckoutStarted({
        products: orderSummaryForCheckout.items,
        experiments,
      });
    }
  }, [
    orderSummaryForCheckout,
    isLoadingOrderSummaryForCheckout,
    user,
    isPaymentOK,
    experiments,
    navigate,
    basePath,
    trackCheckoutStarted,
  ]);

  const onBeforeRedirect = () => {
    return Promise.resolve(returnUrl as string);
  };

  const removeNotAvailableProductsAndPay = useCallback(
    (stockoutItems: Product[]) => {
      removeProductFromCheckout(stockoutItems, () => {
        setStockOutModalVisible(false);

        startCheckout();

        trackRemoveAndPayStockOutModal({
          variantId: stockoutItems ? stockoutItems.map((item) => item.variantId).join(",") : "",
          products: stockoutItems,
        });
      });
    },
    [removeProductFromCheckout, startCheckout, trackRemoveAndPayStockOutModal],
  );

  const trackStockOutModalClose = useCallback(() => {
    trackCloseStockOutModal();
  }, [trackCloseStockOutModal]);

  const closeAndTrackStockoutModal = useCallback(() => {
    setStockOutModalVisible(false);

    trackStockOutModalClose();
  }, [trackStockOutModalClose]);

  const trackStockoutMocalCloseAndGoToHome = useCallback(() => {
    trackStockOutModalClose();

    navigate(-1);
  }, [navigate, trackStockOutModalClose]);

  const onLoadStockOutModal = useCallback(
    (stockoutItems: Product[]) => {
      trackShowStockOutModal({
        products: stockoutItems,
        variantId: stockoutItems ? stockoutItems.map((item: Product) => item.variantId).join(",") : "",
      });
    },
    [trackShowStockOutModal],
  );

  const discardPromo = useDiscardPromo();
  const showPromoError = useCallback(
    (error: PromoCodeError) => {
      const localizedKey = error.localise;
      const localizedMessage = localizedKey ? formatMessage(localizedKey) : "";

      discardPromo(
        () => {
          toast.showError({ message: localizedMessage });
        },
        () => {
          toast.showError({ message: localizedMessage });
        },
      );

      const promocodeRegex = /Promotion with name (.*) not exist/;
      const promocodeResult = error.errorMessage?.match(promocodeRegex);
      const promocode = promocodeResult && promocodeResult.length ? (promocodeResult[1] as string) : "";

      trackPromocodeInvalid({
        promocode,
      });
    },
    [formatMessage, discardPromo, trackPromocodeInvalid, toast],
  );

  const showPromoSuccess = useCallback(
    (response: PromoCode) => {
      const localizedKey = response.localise;
      const localizedMessage = localizedKey ? formatMessage(localizedKey) : "";
      toast.showSuccess({ message: localizedMessage });

      trackPromocodeValid({
        promocode: response.name,
      });
    },
    [formatMessage, toast, trackPromocodeValid],
  );

  const applyPromo = useApplyPromo({
    site: user.country,
    device: Platform.OS === OS.WEB ? "web" : "app",
    promo: {
      code: orderSummaryForCheckout.promo.code,
      discountPercentage: orderSummaryForCheckout.promo.percentage,
    },
    promoCode: { value: orderSummaryForCheckout.promo.code ?? "" },
    onSuccess: showPromoSuccess,
    onError: showPromoError,
  });

  const handleOnClose = useCallback(() => navigate(`${basePath}/${Routes.CART}`), [basePath, navigate]);

  return (
    <CheckoutTemplate
      isCheckoutDisabled={isCheckoutDisabled}
      isLoading={isLoadingOrderSummaryForCheckout}
      layout={layout}
      orderSummary={orderSummaryForCheckout}
      paymentFlowRef={paymentFlowRef}
      paymentInstrumentSelectRef={paymentInstrumentSelectRef}
      shippingAddress={shippingAddress}
      stockOutModalVisible={stockOutModalVisible}
      user={user}
      onAcceptStockoutModalWhenEveryItemStockout={trackStockoutMocalCloseAndGoToHome}
      onAcceptStockoutModalWhenSomeItemStockout={removeNotAvailableProductsAndPay}
      onBeforeRedirect={onBeforeRedirect}
      onClose={handleOnClose}
      onCloseStockoutModal={closeAndTrackStockoutModal}
      onDeleteProduct={onDeleteProduct}
      onErrorPromocode={showPromoError}
      onLoadStockOutModal={onLoadStockOutModal}
      onPressCheckout={startCheckout}
      onValidatePromocode={applyPromo}
    />
  );
};

export { Checkout };
